import type React from "react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePublicSurveyInfo, useSubmitSurveyResponse } from "./useSurvey";
import { survey as surveyNs } from "@duress/shared";
import type { survey } from "@duress/shared";

const SURVEY_QUESTIONS = surveyNs.SURVEY_QUESTIONS;
const SURVEY_SCALE_LABELS = surveyNs.SURVEY_SCALE_LABELS;

export function SurveyPage(): React.JSX.Element {
  const { token = "" } = useParams<{ token: string }>();
  const { data: info, isLoading, error } = usePublicSurveyInfo(token);

  if (isLoading) {
    return <SurveyShell><LoadingState /></SurveyShell>;
  }

  if (error || !info) {
    return (
      <SurveyShell>
        <div className="text-center py-16">
          <p className="text-lg font-semibold">Survey not found</p>
          <p className="text-sm text-muted-foreground mt-2">
            This link is invalid or has expired.
          </p>
        </div>
      </SurveyShell>
    );
  }

  if (info.alreadyCompleted) {
    return (
      <SurveyShell clientName={info.clientName}>
        <div className="flex flex-col items-center text-center py-16 gap-4">
          <CheckCircle className="h-12 w-12 text-green-500" />
          <p className="text-lg font-semibold">Thank you — this survey has already been submitted.</p>
          <p className="text-sm text-muted-foreground">We appreciate your feedback.</p>
        </div>
      </SurveyShell>
    );
  }

  return (
    <SurveyShell clientName={info.clientName}>
      <SurveyForm token={token} />
    </SurveyShell>
  );
}

function SurveyForm({ token }: { token: string }): React.JSX.Element {
  const [scores, setScores] = useState<(number | null)[]>(
    new Array<null>(SURVEY_QUESTIONS.length).fill(null),
  );
  const [name, setName] = useState("");
  const [comments, setComments] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const { mutate: submit, isPending, error } = useSubmitSurveyResponse(token);

  const allAnswered = scores.every((s) => s !== null);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!allAnswered) return;
    const typedScores = scores as number[];
    submit(
      {
        respondentName: name.trim() || null,
        comments: comments.trim() || null,
        scores: typedScores as survey.SubmitSurveyRequest["scores"],
      },
      { onSuccess: () => setSubmitted(true) },
    );
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center text-center py-16 gap-4">
        <CheckCircle className="h-12 w-12 text-green-500" />
        <p className="text-lg font-semibold">Thank you for your feedback!</p>
        <p className="text-sm text-muted-foreground">
          Your responses help us continue improving.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-6">
        {SURVEY_QUESTIONS.map((question, i) => (
          <QuestionRow
            key={i}
            index={i}
            question={question}
            value={scores[i] ?? null}
            onChange={(v) => {
              setScores((prev) => {
                const next = [...prev];
                next[i] = v;
                return next;
              });
            }}
          />
        ))}
      </div>

      <div className="border-t border-border pt-6 space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium" htmlFor="respondent-name">
            Your name <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <input
            id="respondent-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Smith"
            className="flex h-9 w-full max-w-sm rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium" htmlFor="comments">
            Additional comments <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <textarea
            id="comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={4}
            placeholder="Anything else you'd like to share…"
            className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
          />
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "Submission failed. Please try again."}
        </p>
      )}

      {!allAnswered && (
        <p className="text-xs text-muted-foreground">Please answer all questions to submit.</p>
      )}

      <Button type="submit" disabled={!allAnswered || isPending} size="lg">
        {isPending ? "Submitting…" : "Submit feedback"}
      </Button>
    </form>
  );
}

function QuestionRow({
  index,
  question,
  value,
  onChange,
}: {
  index: number;
  question: string;
  value: number | null;
  onChange: (v: number) => void;
}): React.JSX.Element {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium leading-snug">
        <span className="text-muted-foreground mr-1">{index + 1}.</span> {question}
      </p>
      <div role="radiogroup" aria-label={question} className="flex items-center gap-2 flex-wrap">
        {([1, 2, 3, 4, 5] as const).map((score) => {
          const selected = value === score;
          return (
            <button
              key={score}
              type="button"
              onClick={() => onChange(score)}
              aria-pressed={selected}
              aria-label={`${score} — ${SURVEY_SCALE_LABELS[score] ?? ""}`}
              className={`flex flex-col items-center gap-1 px-4 py-2.5 rounded-md border text-sm font-medium transition-colors
                ${selected
                  ? "bg-foreground text-background border-foreground ring-2 ring-foreground ring-offset-1"
                  : "border-border hover:bg-accent hover:text-accent-foreground"
                }`}
            >
              <span>{score}</span>
              <span className="text-[10px] font-normal opacity-70 whitespace-nowrap hidden sm:block">
                {SURVEY_SCALE_LABELS[score]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SurveyShell({
  clientName,
  children,
}: {
  clientName?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-card">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-3">
          <svg viewBox="0 0 93.5 110.5" className="h-6 w-6 text-foreground" aria-hidden="true" fill="currentColor">
            <circle cx="29.22" cy="46.75" r="5.84"/>
            <circle cx="46.75" cy="46.75" r="5.84"/>
            <circle cx="64.28" cy="46.75" r="5.84"/>
            <path d="M79.81,13.69C71.34,5.23,59.66,0,46.75,0S22.15,5.23,13.69,13.69C5.24,22.15,0,33.84,0,46.75s5.24,24.6,13.69,33.06l29.03,29.03c2.23,2.23,5.84,2.23,8.06,0l29.03-29.03c8.45-8.46,13.69-20.15,13.69-33.06s-5.24-24.6-13.69-33.06ZM46.75,85.44c-21.37,0-38.7-17.32-38.7-38.7S25.38,8.05,46.75,8.05s38.7,17.32,38.7,38.7-17.32,38.7-38.7,38.7Z"/>
          </svg>
          <span className="font-head font-semibold text-sm">Duress</span>
          {clientName && (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="text-sm text-muted-foreground">{clientName}</span>
            </>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10">
        {clientName && (
          <div className="mb-8">
            <h1 className="text-2xl font-head font-semibold">Customer Satisfaction Survey</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Please rate each statement from 1 (Strongly Disagree) to 5 (Strongly Agree).
            </p>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

function LoadingState(): React.JSX.Element {
  return (
    <div className="flex items-center justify-center h-64">
      <span className="text-sm text-muted-foreground">Loading survey…</span>
    </div>
  );
}
